import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, FileText, Stethoscope, ArrowRight, Loader2, IdCard, KeyRound, ShieldCheck, Unlink, HeartPulse } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { api } from '../lib/api';
import { getUser, isUserLoggedIn } from '../lib/auth';
import { t } from '../i18n';

export default function Dashboard() {
  const nav = useNavigate();
  const lang = localStorage.getItem('hb_lang') || 'en';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileName, setProfileName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [abhaOpen, setAbhaOpen] = useState(false);
  const [abhaStep, setAbhaStep] = useState('id');
  const [abhaId, setAbhaId] = useState('');
  const [abhaOtp, setAbhaOtp] = useState('');
  const [abhaBusy, setAbhaBusy] = useState(false);

  useEffect(() => {
    if (!isUserLoggedIn()) return nav('/login');
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get('/dashboard/me');
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  if (loading) return <div className="max-w-5xl mx-auto px-4 py-16 text-center text-[#7A7A7A]"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />{t(lang,'loading_dashboard')}</div>;

  const active = data?.active_session;
  const report = data?.latest_report;
  const ref = data?.latest_referral;
  const user = getUser();
  const dashboardUser = data?.user || user || {};
  const abha = dashboardUser?.abha;
  const needsName = /^User \d{4}$/.test(user?.name || '');

  const saveProfileName = async (e) => {
    e.preventDefault();
    if (profileName.trim().length < 2) return;
    setSavingName(true);
    try {
      const { data: res } = await api.post('/auth/profile', { name: profileName.trim() });
      localStorage.setItem('hb_user', JSON.stringify(res.user));
      window.location.reload();
    } finally {
      setSavingName(false);
    }
  };

  const requestAbhaOtp = async (e) => {
    e.preventDefault();
    setAbhaBusy(true);
    try {
      await api.post('/abha/link/request', { abha_id: abhaId.trim() });
      setAbhaStep('otp');
      toast.success('ABDM OTP sent to the mobile linked with this ABHA');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start ABHA link');
    } finally {
      setAbhaBusy(false);
    }
  };

  const verifyAbhaOtp = async (e) => {
    e.preventDefault();
    setAbhaBusy(true);
    try {
      const { data: res } = await api.post('/abha/link/verify', { abha_id: abhaId.trim(), otp: abhaOtp.trim() });
      localStorage.setItem('hb_user', JSON.stringify(res.user));
      setData((prev) => ({ ...(prev || {}), user: res.user }));
      setAbhaOpen(false);
      setAbhaStep('id');
      setAbhaId('');
      setAbhaOtp('');
      toast.success('ABHA linked');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid ABHA OTP');
    } finally {
      setAbhaBusy(false);
    }
  };

  const unlinkAbha = async () => {
    setAbhaBusy(true);
    try {
      const { data: res } = await api.post('/abha/unlink');
      localStorage.setItem('hb_user', JSON.stringify(res.user));
      setData((prev) => ({ ...(prev || {}), user: res.user }));
      toast.success('ABHA removed from Health Buddy');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not unlink ABHA');
    } finally {
      setAbhaBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 fade-up">
      <h1 className="font-display text-4xl text-[#1A1A1A]">Hi {user?.name || 'there'}</h1>
      <p className="text-[#4A4A4A] mt-2">{t(lang,'dashboard_sub')}</p>

      <Dialog open={needsName}>
        <DialogContent className="rounded-3xl border border-[#E8E1D5] bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A1A1A]">What should we call you?</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveProfileName} className="space-y-4">
            <div>
              <Label className="font-semibold text-[#1E3F33]">Your name</Label>
              <Input value={profileName} onChange={(e) => setProfileName(e.target.value)} placeholder="Pawan" className="mt-2 h-12 rounded-xl border-[#E2E8F0]" autoFocus />
            </div>
            <Button disabled={savingName || profileName.trim().length < 2} className="w-full h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
              {savingName ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Continue
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5 mt-8">
        <Card className="p-6 rounded-3xl border border-[#E8E1D5]">
          <div className="flex items-center gap-2 text-[#1E3F33]"><Activity className="h-5 w-5" /> {t(lang,'active_consultation')}</div>
          <div className="mt-3 text-sm text-[#4A4A4A]">{active ? t(lang,'active_consultation_yes') : t(lang,'active_consultation_no')}</div>
          <Link to="/chat"><Button className="mt-4 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white">{active ? t(lang,'resume_chat') : t(lang,'start_chat')}</Button></Link>
        </Card>

        <Card className="p-6 rounded-3xl border border-[#E8E1D5]">
          <div className="flex items-center gap-2 text-[#1E3F33]"><FileText className="h-5 w-5" /> {t(lang,'latest_report')}</div>
          <div className="mt-3 text-sm text-[#4A4A4A]">{report ? report.summary?.slice(0, 90) : t(lang,'no_reports_yet')}</div>
          {report ? <Link to={`/report/${report.id}`}><Button variant="outline" className="mt-4 rounded-full border-[#1E3F33] text-[#1E3F33]">{t(lang,'open_report')} <ArrowRight className="h-4 w-4 ml-1" /></Button></Link> : null}
        </Card>

        <Card className="p-6 rounded-3xl border border-[#E8E1D5]">
          <div className="flex items-center gap-2 text-[#1E3F33]"><Stethoscope className="h-5 w-5" /> {t(lang,'doctor_consultations')}</div>
          <div className="mt-3 text-sm text-[#4A4A4A]">{ref ? `${t(lang,'latest_status')}: ${ref.status}` : t(lang,'no_doctor_consultation')}</div>
          <Link to="/consultations"><Button variant="outline" className="mt-4 rounded-full border-[#1E3F33] text-[#1E3F33]">{t(lang,'view_consultations')}</Button></Link>
        </Card>

        <Card data-testid="abha-link-card" className="p-6 rounded-3xl border border-[#E8E1D5]">
          <div className="flex items-center gap-2 text-[#1E3F33]"><IdCard className="h-5 w-5" /> ABHA</div>
          <div className="mt-3 text-sm text-[#4A4A4A]">
            {abha?.status === 'linked' ? `Linked with ABDM: ${abha.masked}` : 'ABHA not linked yet'}
          </div>
          <p className="mt-2 text-xs leading-5 text-[#7A7A7A]">
            ABHA helps match your identity across ABDM-enabled providers and prepares Health Buddy for consent-based record sharing.
          </p>
          {abha?.status === 'linked' ? (
            <Button onClick={unlinkAbha} disabled={abhaBusy} variant="outline" className="mt-4 rounded-full border-[#1E3F33] text-[#1E3F33]">
              {abhaBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />}
              Remove
            </Button>
          ) : (
            <Button onClick={() => setAbhaOpen(true)} variant="outline" className="mt-4 rounded-full border-[#1E3F33] text-[#1E3F33]">
              <ShieldCheck className="h-4 w-4 mr-2" />
              Link ABHA
            </Button>
          )}
        </Card>

        <Card className="p-6 rounded-3xl border border-[#E8E1D5]">
          <div className="flex items-center gap-2 text-[#1E3F33]"><HeartPulse className="h-5 w-5" /> Health Hub</div>
          <div className="mt-3 text-sm text-[#4A4A4A]">Medication, records, risk scores, wearable metrics, fitness, emergency, and mental health tools.</div>
          <Link to="/health-hub">
            <Button variant="outline" className="mt-4 rounded-full border-[#1E3F33] text-[#1E3F33]">Open hub</Button>
          </Link>
        </Card>
      </div>

      <Dialog open={abhaOpen} onOpenChange={setAbhaOpen}>
        <DialogContent className="rounded-3xl border border-[#E8E1D5] bg-white">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A1A1A]">Link ABHA</DialogTitle>
            <DialogDescription>
              Verify your ABHA with ABDM and attach it to your Health Buddy account for identity matching and future record exchange.
            </DialogDescription>
          </DialogHeader>

          {abhaStep === 'id' ? (
            <form onSubmit={requestAbhaOtp} className="space-y-4">
              <div>
                <Label className="font-semibold text-[#1E3F33]">ABHA number or address</Label>
                <div className="relative mt-2">
                  <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
                  <Input
                    value={abhaId}
                    onChange={(e) => setAbhaId(e.target.value)}
                    placeholder="12-3456-7890-12 or name@abdm"
                    className="h-12 pl-10 rounded-xl border-[#E2E8F0]"
                    autoFocus
                  />
                </div>
                <p className="mt-2 text-xs leading-5 text-[#7A7A7A]">
                  Health Buddy will ask ABDM for supported authentication modes and send an OTP through ABDM only.
                </p>
              </div>
              <Button disabled={abhaBusy || abhaId.trim().length < 3} className="w-full h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
                {abhaBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Send OTP
              </Button>
            </form>
          ) : (
            <form onSubmit={verifyAbhaOtp} className="space-y-4">
              <div>
                <Label className="font-semibold text-[#1E3F33]">OTP</Label>
                <div className="relative mt-2">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A7A7A]" />
                  <Input
                    value={abhaOtp}
                    onChange={(e) => setAbhaOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6 digit OTP"
                    className="h-12 pl-10 rounded-xl border-[#E2E8F0]"
                    inputMode="numeric"
                    autoFocus
                  />
                </div>
                <p className="mt-2 text-xs leading-5 text-[#7A7A7A]">
                  Enter the OTP sent by ABDM to the mobile number registered with this ABHA.
                </p>
              </div>
              <div className="flex gap-2">
                <Button type="button" onClick={() => setAbhaStep('id')} variant="outline" className="h-12 rounded-full border-[#1E3F33] text-[#1E3F33]">
                  Back
                </Button>
                <Button disabled={abhaBusy || abhaOtp.length !== 6} className="flex-1 h-12 rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white font-semibold">
                  {abhaBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                  Verify and link
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
